import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FolderOpen, Users, FileText, DollarSign, Crown, TrendingUp, RefreshCw,
  Calendar, CheckCircle, AlertCircle, ArrowUpRight, UserPlus, FolderPlus, ArrowRight,
  Clock, AlertTriangle, Zap,
} from "lucide-react";
import { useAuth } from "../../../auth/AuthContext";
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";
import { FaCrown } from "react-icons/fa6";
import { injectUserStyles, statusBadgeClass, fmtStatus } from "../../userStyles";

const STYLE_ID = "co-dash-styles-v2";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .co-plan-card {
      background:#0c1118; border-radius:10px; padding:20px 24px; margin-bottom:24px;
    }
    .co-plan-top {
      display:flex; align-items:flex-start; justify-content:space-between;
      flex-wrap:wrap; gap:12px; margin-bottom:16px;
    }
    .co-plan-name {
      font-family:'DM Sans',sans-serif;
      font-size:1.6rem; font-weight:700; color:#fff; line-height:1.15;
    }
    .co-plan-label {
      font-family:'DM Sans',sans-serif;
      font-size:9px; letter-spacing:.04em;
      color:rgba(255,255,255,.35); margin-bottom:4px;
    }
    .co-plan-renewal {
      display:flex; align-items:center; gap:5px;
      font-size:12px; color:rgba(255,255,255,.5); margin-top:4px;
    }
    .co-trial-badge {
      display:inline-flex; align-items:center; gap:5px;
      padding:3px 10px; border-radius:20px;
      font-family:'DM Sans',sans-serif; font-size:10px;
      letter-spacing:.04em; font-weight:700;
      background:rgba(255,255,255,.12); color:#ffffff;
      border:1px solid rgba(255,255,255,.25); margin-top:6px;
    }
    .co-trial-badge.warning {
      background:rgba(239,68,68,.15); color:#fca5a5;
      border-color:rgba(239,68,68,.35);
      animation: co-pulse 1.8s ease-in-out infinite;
    }
    @keyframes co-pulse {
      0%,100% { opacity:1; }
      50%      { opacity:.65; }
    }
    .co-trial-warn-bar {
      border-radius:8px; padding:10px 14px; margin-bottom:10px;
      display:flex; align-items:center; gap:10px;
      background:rgba(239,68,68,.12); border:1px solid rgba(239,68,68,.3);
    }
    .co-plan-card.expired {
      border:1px solid rgba(239,68,68,.35);
    }
    .co-usage-bar-track {
      height:4px; background:rgba(255,255,255,.15); border-radius:2px; overflow:hidden;
    }
    .co-usage-bar-fill { height:100%; border-radius:2px; transition:width .4s; }
    .co-usage-label {
      display:flex; justify-content:space-between;
      font-family:'DM Sans',sans-serif; font-size:10px;
      color:rgba(255,255,255,.4); margin-bottom:5px; letter-spacing:.04em;
    }
    .co-features-row {
      display:flex; flex-wrap:wrap; gap:12px; margin-top:14px; padding-top:14px;
      border-top:1px solid rgba(255,255,255,.08);
    }
    .co-feat {
      display:flex; align-items:center; gap:5px;
      font-size:11.5px; color:rgba(255,255,255,.6);
    }
    .co-feat-active   { color:rgba(255,255,255,.85); }
    .co-feat-inactive { opacity:.4; }

    .co-recent-item {
      display:flex; align-items:flex-start; justify-content:space-between;
      padding:11px 20px; border-bottom:1px solid #f3f4f6;
      cursor:pointer; transition:background 150ms;
    }
    .co-recent-item:last-child { border-bottom:none; }
    .co-recent-item:hover { background:#fafafa; }
    .co-recent-name { font-size:13.5px; font-weight:500; color:#0c1118; }
    .co-recent-addr { font-size:11px; color:#9ca3af; margin-top:1px; }
    .co-recent-date { font-family:'DM Sans',sans-serif; font-size:10px; color:#d1d5db; margin-top:3px; }
  `;
  document.head.appendChild(el);
}

export default function CompanyDashboard() {
  const [loading, setLoading]             = useState(true);
  const [projects, setProjects]           = useState({});
  const [quotes, setQuotes]               = useState({});
  const [staff, setStaff]                 = useState({});
  const [billing, setBilling]             = useState(null);
  const [orgBilling, setOrgBilling]       = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [recentQuotes, setRecentQuotes]   = useState([]);
  const [lastUpdated, setLastUpdated]     = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [features, setFeatures]           = useState(null);
  const [subplan, setSubplan]             = useState(null);

  useEffect(() => { injectLocal(); }, []);

  async function load() {
    setLoading(true);
    try {
      const [resp, obResp] = await Promise.all([
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dashboard/company`, { credentials: "include" }),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/org-billing/me`,    { credentials: "include" }),
      ]);
      if (resp.ok) {
        const data = await resp.json();
        setProjects(data.projects);
        setQuotes(data.quotes);
        setStaff(data.staff);
        setBilling(data.billing);
        setRecentProjects(data.recentProjects ?? []);
        setRecentQuotes(data.recentQuotes ?? []);
      }
      if (obResp.ok) {
        setOrgBilling(await obResp.json());
      }
      setLastUpdated(new Date());
    } catch {
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);
  useEffect(() => { setFeatures(user.features); setSubplan(user.plan); }, [user]);

  // ── Trial state — derived from orgBilling.trial (free trial on Org record) ──
  const isTrialing      = orgBilling?.trial?.isActive === true;
  const isExpiredTrial  = orgBilling?.trial?.isExpired === true && !orgBilling?.hasActiveSubscription;
  const trialEndsAt     = orgBilling?.trial?.trialEndsAt ? new Date(orgBilling.trial.trialEndsAt) : null;
  const trialDaysLeft   = trialEndsAt
    ? Math.max(0, Math.ceil((trialEndsAt - new Date()) / 86_400_000))
    : null;
  const trialWarning    = trialDaysLeft !== null && trialDaysLeft <= 1;

  const planName = isExpiredTrial
    ? "Trial Expired"
    : isTrialing
      ? "Free Trial"
      : subplan?.name || billing?.subscription?.plan?.name || "Free";
  const maxProjects     = Number(features?.["org.projects.max"] ?? 5);
  const projectUsage    = maxProjects > 0 ? (projects.active / maxProjects) * 100 : 0;
  const nearLimit       = projects.active >= Math.max(1, Math.floor(maxProjects * 0.8));
  const hasBranding     = !!features?.["feat.branding"];
  const hasAutoEstimator = !!features?.["feat.estimator"];
  const has2D           = !!features?.["feat.2d"];
  const has3D           = !!features?.["feat.3d"];

  const projectChartData = [
    { name: "Active",   value: projects.active,   color: "#0c1118" },
    { name: "Draft",    value: projects.draft,     color: "#9ca3af" },
    { name: "Archived", value: projects.archived,  color: "#9ca3af" },
  ].filter((d) => d.value > 0);

  const quoteChartData = [
    { name: "Pending",   value: quotes.pending,                     color: "#555555" },
    { name: "Completed", value: (quotes.total ?? 0) - (quotes.pending ?? 0), color: "#0c1118" },
  ].filter((d) => d.value > 0);

  const barChartData = [
    { category: "Projects", count: projects.total ?? 0 },
    { category: "Quotes",   count: quotes.total   ?? 0 },
    { category: "Team",     count: staff.total     ?? 0 },
  ];

  const KPIS = [
    { accent: "#0c1118", val: projects.total,  lbl: "Total Projects",  nav: "/dashboard/projects" },
    { accent: "#111111", val: projects.active, lbl: "Active Projects",  nav: "/dashboard/projects?status=active" },
    { accent: "#6366f1", val: quotes.total,    lbl: "Total Quotes",     nav: "/dashboard/quotes-org" },
    { accent: "#1d9e75", val: staff.total,     lbl: "Team Members",     nav: "/dashboard/staff" },
  ];

  const FEATURES = [
    { label: "Custom Branding",    active: hasBranding },
    { label: "Auto Estimator",     active: hasAutoEstimator },
    { label: "3D Design",          active: has3D },
    { label: "2D Design",          active: has2D },
    { label: "Team Collaboration", active: true },
    { label: "Advanced Reports",   active: planName !== "Free" },
    { label: "Purchase Plan",      active: true },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", marginBottom: "24px" }}>
        <div>
          <div className="up-title">Company Dashboard</div>
          <div className="up-sub">
            Organization overview &nbsp;·&nbsp;
            {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : "Loading…"}
          </div>
        </div>
        <button className="up-btn up-btn-outline" onClick={load} disabled={loading}>
          <RefreshCw size={14} style={{ animation: loading ? "upSpin .7s linear infinite" : "none" }} />
          Refresh
        </button>
      </div>

      {/* Plan card */}
      <div className={`co-plan-card${isExpiredTrial ? " expired" : ""}`}>
        <div className="co-plan-top">
          <div>
            <div className="co-plan-label">Current Plan</div>
            <div className="co-plan-name" style={isExpiredTrial ? { color: "#fca5a5" } : undefined}>{planName}</div>
            {isExpiredTrial && trialEndsAt ? (
              <div className="co-trial-badge warning">
                <Clock size={10} />
                {`Expired ${trialEndsAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`}
              </div>
            ) : isTrialing && trialEndsAt ? (
              <div className={`co-trial-badge${trialWarning ? " warning" : ""}`}>
                <Clock size={10} />
                {trialDaysLeft === 0
                  ? "Expires today"
                  : trialDaysLeft === 1
                    ? "1 day remaining"
                    : `${trialDaysLeft} days remaining`}
                {" · "}
                {trialEndsAt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </div>
            ) : (
              <div className="co-plan-renewal">
                <Calendar size={12} />
                {billing?.nextBillingDate
                  ? `Renews ${new Date(billing.nextBillingDate).toLocaleDateString()}`
                  : !isTrialing ? "Active subscription" : null}
              </div>
            )}
          </div>
          <Link to="/dashboard/subscription" className="up-btn" style={{ background: "#ffffff", color: "#111111" }}>
            <FaCrown size={13} /> {(isTrialing || isExpiredTrial) ? "Upgrade Plan" : "Manage Plan"}
          </Link>
        </div>

        {/* Expired trial bar inside card */}
        {isExpiredTrial && (
          <div className="co-trial-warn-bar" style={{ marginBottom: 14 }}>
            <AlertTriangle size={14} style={{ color: "#f87171", flexShrink: 0 }} />
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: "#fca5a5", flex: 1 }}>
              Your company free trial has expired. Subscribe to restore your team&apos;s access.
            </div>
            <Link to="/dashboard/subscription" style={{
              background: "#ef4444", color: "#fff", border: "none",
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "6px 14px", borderRadius: 7,
              fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 700,
              textDecoration: "none", flexShrink: 0,
            }}>
              <Zap size={12} /> Upgrade Now
            </Link>
          </div>
        )}

        {/* Trial warning bar inside card — 1 day or less */}
        {isTrialing && trialWarning && (
          <div className="co-trial-warn-bar">
            <AlertTriangle size={14} style={{ color: "#f87171", flexShrink: 0 }} />
            <div style={{ fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: "#fca5a5", flex: 1 }}>
              {trialDaysLeft === 0
                ? "Your company free trial expires today. Subscribe to keep your team's access."
                : "Your company free trial expires tomorrow. Subscribe to avoid interruption."}
            </div>
            <Link to="/dashboard/subscription" style={{
              background: "#ef4444", color: "#fff", border: "none",
              display: "inline-flex", alignItems: "center", gap: 5,
              padding: "6px 14px", borderRadius: 7,
              fontFamily: "'DM Sans',sans-serif", fontSize: 12, fontWeight: 700,
              textDecoration: "none", flexShrink: 0,
            }}>
              <Zap size={12} /> Upgrade
            </Link>
          </div>
        )}

        {/* Usage bar */}
        <div className="co-usage-label">
          <span>Project Usage</span>
          <span>{projects.total ?? 0} / {maxProjects}</span>
        </div>
        <div className="co-usage-bar-track">
          <div
            className="co-usage-bar-fill"
            style={{ width: `${Math.min(projectUsage, 100)}%`, background: nearLimit ? "#ef4444" : "#ffffff" }}
          />
        </div>
        {nearLimit && (
          <div style={{ display: "flex", alignItems: "center", gap: "5px", fontSize: "11px", color: "#fca5a5", marginTop: "5px" }}>
            <AlertCircle size={11} /> Near project limit — consider upgrading
          </div>
        )}

        {/* Feature flags */}
        <div className="co-features-row">
          {FEATURES.map((f) => (
            <div key={f.label} className={`co-feat ${f.active ? "co-feat-active" : "co-feat-inactive"}`}>
              <CheckCircle size={12} style={{ color: f.active ? "#10b981" : "#ef4444" }} />
              {f.label}
            </div>
          ))}
        </div>
      </div>

      {/* KPI row */}
      <div className="up-kpi-grid">
        {KPIS.map((k) => (
          <div
            className="up-kpi"
            key={k.lbl}
            style={{ cursor: "pointer" }}
            onClick={() => navigate(k.nav)}
          >
            <div className="up-kpi-accent" style={{ background: k.accent }} />
            <div className="up-kpi-val">{loading ? "—" : (k.val ?? 0)}</div>
            <div className="up-kpi-lbl">{k.lbl}</div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="up-three-col" style={{ marginBottom: "20px" }}>
        <div className="up-panel">
          <div className="up-panel-head"><div className="up-panel-title">Project Distribution</div></div>
          <div style={{ padding: "16px" }}>
            {projectChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={projectChartData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value">
                    {projectChartData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="up-empty"><div className="up-empty-sub">No data yet</div></div>
            )}
          </div>
        </div>

        <div className="up-panel">
          <div className="up-panel-head"><div className="up-panel-title">Quote Pipeline</div></div>
          <div style={{ padding: "16px" }}>
            {quoteChartData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie data={quoteChartData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value">
                    {quoteChartData.map((e, i) => <Cell key={i} fill={e.color} />)}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="up-empty"><div className="up-empty-sub">No data yet</div></div>
            )}
          </div>
        </div>

        <div className="up-panel">
          <div className="up-panel-head"><div className="up-panel-title">Overview Summary</div></div>
          <div style={{ padding: "16px" }}>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={barChartData}>
                <XAxis dataKey="category" tick={{ fontSize: 11, fontFamily: "'DM Sans', sans-serif" }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#0c1118" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Recent lists + quick actions */}
      <div className="up-three-col">
        {/* Recent Projects */}
        <div className="up-panel">
          <div className="up-panel-head">
            <div className="up-panel-title">Recent Projects</div>
            <Link to="/dashboard/projects" className="up-panel-link">View all <ArrowRight size={12} /></Link>
          </div>
          {loading ? (
            <div className="up-loading"><div className="up-spinner" /> Loading…</div>
          ) : recentProjects.length === 0 ? (
            <div className="up-empty"><div className="up-empty-sub">No projects yet</div></div>
          ) : recentProjects.map((item) => (
            <div
              className="co-recent-item"
              key={item.id}
              onClick={() => navigate(`/dashboard/projects/${item.id}`)}
            >
              <div style={{ minWidth: 0 }}>
                <div className="co-recent-name">{item.name}</div>
                <div className="co-recent-addr">
                  {[item.street, item.city, item.state].filter(Boolean).join(", ")}
                </div>
                <div className="co-recent-date">{new Date(item.updatedAt || item.createdAt).toLocaleDateString()}</div>
              </div>
              <span className={statusBadgeClass(item.status)} style={{ marginLeft: "8px", flexShrink: 0 }}>
                {fmtStatus(item.status)}
              </span>
            </div>
          ))}
        </div>

        {/* Recent Quotes */}
        <div className="up-panel">
          <div className="up-panel-head">
            <div className="up-panel-title">Recent Quotes</div>
            <Link to="/dashboard/quotes-org" className="up-panel-link">View all <ArrowRight size={12} /></Link>
          </div>
          {loading ? (
            <div className="up-loading"><div className="up-spinner" /> Loading…</div>
          ) : recentQuotes.length === 0 ? (
            <div className="up-empty"><div className="up-empty-sub">No quotes yet</div></div>
          ) : recentQuotes.map((item) => (
            <div
              className="co-recent-item"
              key={item.id}
              onClick={() => navigate(`/dashboard/quotes-org/${item.id}`)}
            >
              <div style={{ minWidth: 0 }}>
                <div className="co-recent-name">{item.project?.name || item.projectId}</div>
                <div className="co-recent-addr">{item.requester?.email}</div>
                <div className="co-recent-date">{new Date(item.createdAt).toLocaleDateString()}</div>
              </div>
              <span className={statusBadgeClass(item.status)} style={{ marginLeft: "8px", flexShrink: 0 }}>
                {fmtStatus(item.status)}
              </span>
            </div>
          ))}
        </div>

        {/* Quick Actions */}
        <div className="up-panel">
          <div className="up-panel-head"><div className="up-panel-title">Quick Actions</div></div>
          {[
            { to: "/dashboard/projects",    icon: FolderPlus, label: "Create New Project" },
            { to: "/dashboard/quotes-org",  icon: FileText,   label: "Request Quote"      },
            { to: "/dashboard/staff",       icon: UserPlus,   label: "Invite Team Member" },
            { to: "/dashboard/subscription",icon: FaCrown,    label: "Manage Subscription"},
          ].map((a) => (
            <Link to={a.to} className="up-qa-link" key={a.to}>
              <div className="up-qa-icon"><a.icon size={16} /></div>
              <div style={{ flex: 1 }}><div className="up-qa-name">{a.label}</div></div>
              <ArrowRight size={15} className="up-qa-arrow" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}